/* eslint-disable prefer-destructuring */
/* eslint-disable no-underscore-dangle -- the __dhlConfig variable is named this way */
import { isNative } from '@dhl/webview'
import {
  type AvailableFeatures,
  isFeatureKeyEnabled,
  buildConfig,
  topLevelDomainUtils,
  type BaseJsonConfiguration,
} from '@dhl/receiver-config'
import {
  type JsonConfiguration,
  portalTlds,
  type PortalTlds,
  type PortalAppConfiguration,
} from '../../configuration/appconfiguration.js'
import localJsonConfig from '../../configuration/local.json' with { type: 'json' }

type PortalInjectedConfig = { CSP_NONCE: string; ENVIRONMENT: string; VERSION: string }

declare global {
  interface Window {
    /* @ts-ignore -- this variable is injected at runtime */
    __dhlConfig: JsonConfiguration<PortalTlds> & PortalInjectedConfig
  }
}

const domainUtils = topLevelDomainUtils.buildDomainUtils<PortalTlds>({
  supportedTlds: portalTlds,
  defaultTld: 'nl',
  tldOverride: process.env.REACT_APP_UTIL_TOP_LEVEL_DOMAIN_OVERRIDE || window.__dhlConfig?.default?.util?.tldOverride,
  frontendDomain: 'my.dhlparcel',
})

const getTopLevelDomain: (hostname?: string) => 'nl' = domainUtils.getTopLevelDomain
const buildConfigForTld: <
  TAppConfiguration,
  TJsonConfig extends BaseJsonConfiguration<readonly ['nl'], TAppConfiguration>,
>(props: {
  config: TJsonConfig
  tld: keyof TJsonConfig
}) => TAppConfiguration = domainUtils.buildConfigForTld

export { getTopLevelDomain, buildConfigForTld }

const buildConfigInstance = (): PortalAppConfiguration & {
  cspNonce: string
  environment: string
  isDevelopment: boolean
  version: string
  features: {
    profile: {
      address: {
        mobileNumberRequired: (isDefaultShippingAddress: boolean) => boolean
      }
    }
  }
  prismic: {
    endpoint: string
  }
} => {
  const tld = getTopLevelDomain()
  const isDevelopment = process.env.NODE_ENV !== 'production'

  // In development mode we use the local config, otherwise get the runtime config (i.e. window.__dhlConfig)
  const baseConfig = buildConfigForTld<PortalAppConfiguration, JsonConfiguration<PortalTlds>>({
    config: isDevelopment ? localJsonConfig : window.__dhlConfig,
    tld,
  })

  // We need to add some config settings that are not yet definable in the JSON config
  const additionalConfig = () => ({
    cspNonce: window.__dhlConfig?.CSP_NONCE ?? '',
    environment: window.__dhlConfig?.ENVIRONMENT ?? 'development',
    isDevelopment,
    version: window.__dhlConfig?.VERSION ?? 'latest',
    features: {
      profile: {
        address: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          mobileNumberRequired: (_isDefaultShippingAddress: boolean) => false,
        },
      },
    },
    prismic: {
      endpoint: `${window.location.origin}${baseConfig.prismic.endpoint}`,
    },
  })

  return buildConfig<PortalAppConfiguration, ReturnType<typeof additionalConfig>>({
    baseConfig,
    additionalConfig,
  })
}

/**
 * Configuration singleton
 */
let configInstance: ReturnType<typeof buildConfigInstance> | null = null

export const getConfig = (): ReturnType<typeof buildConfigInstance> => {
  configInstance ??= buildConfigInstance()
  return configInstance
}

/**
 * Feature enabled utils
 */
type FeatureKeys = AvailableFeatures<PortalAppConfiguration['features']>
export const isFeatureEnabled = (...featureKeys: FeatureKeys[]): boolean => {
  const config = getConfig()
  return featureKeys.some((featureKey) => isFeatureKeyEnabled(featureKey, config))
}

export const isFeatureEnabledNative = (...featureKeys: FeatureKeys[]): boolean => {
  const { isDevelopment } = getConfig()
  return isFeatureEnabled(...featureKeys) && (!!window.Cypress || isNative() || isDevelopment)
}
